import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { configure } from "mobx";
import ruRU from "antd/locale/ru_RU";
import { ConfigProvider } from "antd";
import { observer } from "mobx-react";
import { useEffect } from "react";
import MyApp from "./MyApp";
import themeStore from "./stores/themeStore";
import userStore from "./stores/userStore";
import { applyPrimaryPalette, resolvePrimary } from "./utils/Palette";

dayjs.extend(utc);

configure({
  enforceActions: "never",
});

themeStore.init();

const App = observer(() => {
  const isDark = themeStore.isDark;

  const primary = resolvePrimary(userStore.user?.color, isDark);

  useEffect(() => {
    applyPrimaryPalette(userStore.user?.color);
  }, [isDark]);

  return (
    <ConfigProvider
      locale={ruRU}
      wave={{ disabled: true }}
      renderEmpty={() => <div style={{ marginTop: 20 }} className="emptyPlaceholder" />}
      theme={{
        token: {
          fontFamily: "Inter",
          colorPrimary: primary,
        },
        components: {
          Popover: {
            borderRadius: 12,
            borderRadiusLG: 12,
          },

          Input: {
            colorBgContainer: "var(--colorBgContainer)",
            colorBgContainerDisabled: "var(--colorBgContainerDisabled)",
            colorTextDisabled: "var(--colorTextDisabled)",
            colorBorder: "var(--colorBorder)",
            colorText: "var(--primaryFontColor)",
            controlHeight: 40,
            colorTextPlaceholder: "var(--colorTextPlaceholder)",
          },
          Select: {
            colorBgContainer: "var(--colorBgContainer)",
            colorBgContainerDisabled: "var(--colorBgContainerDisabled)",
            colorTextDisabled: "var(--colorTextDisabled)",
            colorBorder: "var(--colorBorder)",
            controlHeight: 40,
            colorTextPlaceholder: "var(--colorTextPlaceholder)",
            colorText: "var(--primaryFontColor)",
            colorBgElevated: "var(--dropdown)",
            multipleItemBg: "var(--primary10)",
            controlItemBgHover: "var(--primary10)",
            colorBorderDisabled: "var(--colorBorder)",
          },
          DatePicker: {
            colorBgContainer: "var(--colorBgContainer)",
            colorBgContainerDisabled: "var(--colorBgContainerDisabled)",
            colorTextDisabled: "var(--colorTextDisabled)",
            colorBorder: "var(--colorBorder)",
            controlHeight: 40,
            colorTextPlaceholder: "var(--colorTextPlaceholder)",
            colorText: "var(--primaryFontColor)",
            colorBgElevated: "var(--dropdown)",
            colorIcon: "var(--primaryFontColor)",
          },
          InputNumber: {
            colorBgContainer: "var(--colorBgContainer)",
            colorBgContainerDisabled: "var(--colorBgContainerDisabled)",
            colorTextDisabled: "var(--colorTextDisabled)",
            colorBorder: "var(--colorBorder)",
            controlHeight: 40,
            colorTextPlaceholder: "var(--colorTextPlaceholder)",
            colorText: "var(--primaryFontColor)",
          },
          Form: {
            labelColor: "var(--labelColor)",
            itemMarginBottom: 16,
          },
          Table: {
            rowHoverBg: "var(--primary10)",
            rowSelectedHoverBg: "var(--primary10)",
            rowSelectedBg: "var(--primary10)",
            borderRadius: 8,
            borderRadiusLG: 8,
            borderRadiusOuter: 8,
            borderRadiusSM: 8,
            borderRadiusXS: 8,
            headerBg: "transparent",
            headerColor: "var(--primaryFontColor)",
            colorBgContainer: "transparent",
            colorText: "var(--primaryFontColor)",
            headerSortHoverBg: "var(--primary10)",
            headerSortActiveBg: "var(--primary10)",
          },
          Button: {
            borderColorDisabled: "transparent",
            controlHeight: 32,
            fontSize: 14,
            borderRadius: 8,
            defaultBorderColor: primary,
            defaultColor: primary,
            colorText: primary,
            paddingContentHorizontal: 12,
            fontWeight: 500,
          },
          Message: {
            colorIcon: "var(--primary)",
          },
          Modal: {
            titleFontSize: 18,
            colorBgElevated: "var(--drawer)",
            colorIcon: "var(--primaryFontColor)",
            colorPrimary: "var(--primary)",
          },
          Drawer: {
            colorBgElevated: "var(--drawerBack)",
            colorIcon: "var(--primaryFontColor)",
          },
          Tabs: {
            lineWidth: 1.5,
          },

          Menu: {
            itemActiveBg: "transparent",
            itemHoverBg: "transparent",
            itemHoverColor: "var(--segmentHover)",
            itemSelectedBg: "rgb(40, 47, 57)",
            itemSelectedColor: "white",
            itemColor: "var(--segmentLabel)",
            itemBorderRadius: 4,
          },
          Rate: {
            starBg: "var(--night100)",
            starColor: "var(--primary)",
          },
          Dropdown: {
            colorBgElevated: "var(--dropdown)",
            colorText: "var(--primaryFontColor)",
            controlItemBgHover: "var(--primary10)",
          },
          Divider: {
            colorSplit: "var(--night100)",
            colorTextHeading: "var(--primaryFontColor)",
          },
          Tour: {
            colorBgElevated: "var(--dropdown)",
            colorText: "var(--primaryFontColor)",
            colorIcon: "var(--primaryFontColor)",
            colorIconHover: "var(--primary)",
          },
          Card: {
            borderRadiusLG: 12,
            paddingLG: 16,
            extraColor: primary,
            headerFontSize: 14,
            headerPadding: 12,
            bodyPadding: 12,
            headerHeight: 40,
          },
          Checkbox: {
            colorText: "var(--primaryFontColor)",
          },
        },
      }}
    >
      <MyApp />
    </ConfigProvider>
  );
});

export default App;
